import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, Clock, Shield, CheckCircle2, Droplet, WrenchIcon, MapPin, Star } from 'lucide-react';
import { SEO } from '../components/SEO';
import { LocalBusinessSchema } from '../components/SchemaOrg';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    {
      icon: <Phone className="w-12 h-12 text-blue-600" />,
      title: 'Dépannage Urgence 24/7',
      description: 'Fuite d\'eau, canalisation bouchée, panne de chauffe-eau ? Nous intervenons en moins de 30 minutes à Paris 17e et alentours.',
      link: '/urgences',
      image: 'https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxwbHVtYmluZ3xlbnwwfHx8fDE3ODE3NzkwNDR8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <WrenchIcon className="w-12 h-12 text-blue-600" />,
      title: 'Installation Sanitaire',
      description: 'Rénovation complète de salle de bain, installation de cuisine, pose de sanitaires modernes par des artisans qualifiés.',
      link: '/services',
      image: 'https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxwbHVtYmluZ3xlbnwwfHx8fDE3ODE3NzkwNDR8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Droplet className="w-12 h-12 text-blue-600" />,
      title: 'Réparation Fuites',
      description: 'Détection et réparation de fuites invisibles avec équipement professionnel. Intervention rapide pour limiter les dégâts.',
      link: '/services',
      image: 'https://images.unsplash.com/photo-1542013936693-884638332954?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxwbHVtYmluZ3xlbnwwfHx8fDE3ODE3NzkwNDR8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Wrench className="w-12 h-12 text-blue-600" />,
      title: 'Débouchage Professionnel',
      description: 'Débouchage de canalisations, éviers, WC avec inspection vidéo. Garantie de résultat et conseils préventifs.',
      link: '/services',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxwbHVtYmluZ3xlbnwwfHx8fDE3ODE3NzkwNDR8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  const advantages = [
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Plombiers certifiés RGE et assurés décennale' },
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Intervention rapide sous 30 minutes dans Paris 17 et alentours' },
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Devis gratuit et transparent avant intervention' },
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Garantie travaux et pièces de rechange' },
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Service urgence disponible 24h/24, 7j/7' },
    { icon: <CheckCircle2 className="w-6 h-6" />, text: 'Prix compétitifs et paiement facilité' }
  ];

  const testimonials = [
    {
      name: 'Sophie M.',
      location: 'Paris 17e',
      text: 'Fuite d\'eau un dimanche soir, le plombier est arrivé en 25 minutes. Travail propre, prix annoncé respecté. Je recommande vivement NHM Plomberie !',
      service: 'Dépannage urgence'
    },
    {
      name: 'Laurent D.',
      location: 'Levallois-Perret',
      text: 'Remplacement complet de mon chauffe-eau. Devis clair, conseils pertinents sur le modèle, installation impeccable en une demi-journée. Très professionnel.',
      service: 'Remplacement chauffe-eau'
    },
    {
      name: 'Catherine B.',
      location: 'Paris 8e',
      text: 'Canalisation bouchée depuis des semaines, résolu en une heure avec inspection caméra. Explications claires et conseils pour éviter que ça se reproduise. Parfait.',
      service: 'Débouchage canalisation'
    }
  ];

  const zones = [
    'Paris 17e',
    'Paris 8e', 
    'Paris 9e',
    'Paris 16e',
    'Paris 18e',
    'Levallois-Perret',
    'Clichy',
    'Neuilly-sur-Seine'
  ];

  return (
    <>
      <SEO
        title="Plombier Paris 17e 24/7 - Dépannage Urgent | NHM Plomberie"
        description="Plombier professionnel à Paris 17e (75017). Intervention rapide 24h/24 pour urgence, fuite, débouchage. Devis gratuit ☎ 09 70 70 58 63"
        keywords="plombier Paris 17e, plomberie urgence 75017, dépannage plombier, plombier 24/7, fuite eau, débouchage canalisation, NHM Plomberie"
        canonical="https://nhm-plomberie.fr/"
      />
      <LocalBusinessSchema />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-blue-900 opacity-90"></div>
        <img
          src="https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxwbHVtYmVyJTIwcmVwYWlyaW5nJTIwbGVha3xlbnwwfHx8fDE3ODE3ODQ0MDd8MA&ixlib=rb-4.1.0&q=85"
          alt="Plombier professionnel NHM Plomberie réparant une fuite d'eau à Paris 17e"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 to-blue-900/70"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-white">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Plombier à Paris 17e - Intervention 24/7
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Expert en plomberie depuis plus de 10 ans. Dépannage urgent, installation, rénovation. 
              Intervention rapide dans Paris et proche banlieue. Devis gratuit.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6">
                <a href="tel:0970705863">
                  <Phone className="mr-2" />
                  Urgence: 09 70 70 58 63
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
                <Link to="/contact">Devis Gratuit</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 text-center">
              Votre Plombier de Confiance à Paris 17e
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                <strong>NHM Plomberie</strong> est votre entreprise de plomberie locale basée au <strong>18 rue Legendre, 75017 Paris</strong>. 
                Depuis notre création, nous mettons notre expertise au service des particuliers et professionnels des Paris et sa proche banlieue pour tous travaux de plomberie, 
                du simple dépannage à la rénovation complète.
              </p>
              <p>
                Notre équipe de <strong>plombiers qualifiés et certifiés</strong> intervient rapidement dans toute la commune de Paris 17e 
                et les arrondissements et communes limitrophes (Paris 8e, 9e, 16e, 18e, Levallois-Perret, Clichy, Neuilly-sur-Seine). Que vous ayez besoin d'un 
                <strong> dépannage d'urgence pour une fuite d'eau</strong>, d'une <strong>installation de salle de bain</strong>, 
                d'un <strong>débouchage de canalisation</strong> ou d'un <strong>remplacement de chauffe-eau</strong>, nous sommes là pour vous.
              </p>
              <p>
                Disponibles <strong>24 heures sur 24 et 7 jours sur 7</strong>, nos plombiers peuvent intervenir chez vous en moins de 30 minutes 
                en cas d'urgence. Nous utilisons du matériel professionnel de dernière génération et travaillons avec des fournisseurs de confiance 
                pour garantir la qualité et la durabilité de nos interventions. Tous nos travaux sont assurés et garantis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Services de Plomberie</h2>
            <p className="text-xl text-gray-600">Des solutions professionnelles pour tous vos besoins</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none group">
                <CardContent className="p-6">
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <img
                    src={service.image}
                    alt={`Service ${service.title} par NHM Plomberie à Paris 17e`}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                    width="400"
                    height="300"
                  />
                  <Link 
                    to={service.link} 
                    className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all"
                  >
                    En savoir plus →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/services">Découvrir tous nos services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi Choisir NHM Plomberie ?</h2>
            <p className="text-xl text-gray-600">L'expertise et la réactivité au service de votre confort</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="text-blue-600 flex-shrink-0">{advantage.icon}</div>
                <p className="text-lg text-gray-700">{advantage.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-2xl p-8 max-w-4xl mx-auto border-2 border-blue-100">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Notre Engagement Qualité</h3>
            <p className="text-gray-700 leading-relaxed">
              Chez NHM Plomberie, nous nous engageons à vous fournir un <strong>service de qualité irréprochable</strong>. 
              Chaque intervention commence par un diagnostic précis et un devis détaillé gratuit. Nos plombiers prennent le temps 
              de vous expliquer la nature du problème et les solutions possibles. Nous privilégions toujours la transparence et 
              l'honnêteté dans nos relations clients. Nos tarifs sont clairs, sans surprise, et nous acceptons différents moyens 
              de paiement pour votre confort.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Ce que Disent Nos Clients</h2>
            <p className="text-xl text-gray-600">La satisfaction de nos clients est notre meilleure carte de visite</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 hover:border-blue-600 transition-all duration-300 hover:shadow-xl" data-testid={`testimonial-card-${index}`}>
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location} · {testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zone Coverage */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Notre Zone d'Intervention</h2>
            <p className="text-lg text-gray-700 mb-8">
              Basés à Paris 17e, nous intervenons rapidement dans toutes les communes des Paris et sa proche banlieue. 
              Nos délais d'intervention varient de 15 à 30 minutes selon votre localisation.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {zones.map((zone, index) => (
                <span key={index} className="bg-white px-4 py-2 rounded-full text-gray-700 border border-gray-200">
                  {zone}
                </span>
              ))}
            </div>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
              <Link to="/zone-intervention">Voir toute la zone d'intervention</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <Clock className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">Une Urgence Plomberie ?</h2>
          <p className="text-xl mb-2 text-blue-100">Nous sommes disponibles 24h/24, 7j/7 pour toute urgence</p>
          <p className="text-lg mb-8 text-blue-200">Intervention rapide à Paris 17e et dans Paris et proche banlieue</p>
          <Button asChild size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6">
            <a href="tel:0970705863">
              <Phone className="mr-2" />
              Appelez maintenant: 09 70 70 58 63
            </a>
          </Button>
        </div>
      </section>
    </>
  );
};

export default Home;
